import React, { useState } from 'react'
import { Link } from 'react-router-dom'
import { PageHeader } from '@/components/layout'
import { useCourses, usePublishCourse, useUnpublishCourse, useDeleteCourse } from '@/hooks/useCourses'
import { ImageWithFallback } from '@/components/common/ImageWithFallback'
import { apiClient } from '@/services/api'

type CourseType = 'all' | 'pure_online' | 'hybrid'
type CourseStatus = 'all' | 'published' | 'draft'

export const AdminCoursesPage: React.FC = () => {
    const { data: courses, isLoading } = useCourses()
    const publishCourse = usePublishCourse()
    const unpublishCourse = useUnpublishCourse()
    const deleteCourse = useDeleteCourse()
    const [typeFilter, setTypeFilter] = useState<CourseType>('all')
    const [statusFilter, setStatusFilter] = useState<CourseStatus>('all')
    const [searchQuery, setSearchQuery] = useState('')

    const handlePublish = async (courseId: string) => {
        try {
            await publishCourse.mutateAsync(courseId)
        } catch (error) {
            console.error('Failed to publish course:', error)
        }
    }

    const handleUnpublish = async (courseId: string) => {
        try {
            await unpublishCourse.mutateAsync(courseId)
        } catch (error) {
            console.error('Failed to unpublish course:', error)
        }
    }

    const handleDelete = async (courseId: string, courseTitle: string) => {
        const confirmed = window.confirm(
            `Apakah Anda yakin ingin menghapus kursus "${courseTitle}"?\n\nTindakan ini tidak dapat dibatalkan dan semua data terkait (modul, materi, quiz, dll) akan ikut terhapus.`
        )
        if (!confirmed) return

        try {
            await deleteCourse.mutateAsync(courseId)
        } catch (error: any) {
            console.error('Failed to delete course:', error)
            alert(error?.message || 'Gagal menghapus kursus. Silakan coba lagi.')
        }
    }

    const handleExportCSV = async (courseId: string, courseTitle: string) => {
        try {
            const result = await apiClient.get(`/enrollments/course/${courseId}`)
            const enrollments = result.data as any[]
            if (!enrollments?.length) {
                alert('Tidak ada data siswa untuk kursus ini.')
                return
            }

            const headers = ['No', 'Nama', 'Email', 'Gender', 'NIK', 'Status', 'Tanggal Daftar']
            const rows = enrollments.map((e: any, i: number) => [
                i + 1,
                e.name || '',
                e.email || '',
                e.gender || '-',
                e.nik ? `${e.nik} ` : '-',
                e.status,
                e.enrolled_at ? new Date(e.enrolled_at).toLocaleDateString('id-ID') : '-',
            ])

            const csvContent = [
                headers.join(','),
                ...rows.map((r: any[]) => r.map(v => `"${v}"`).join(',')),
            ].join('\n')

            const blob = new Blob(['\ufeff' + csvContent], { type: 'text/csv;charset=utf-8;' })
            const url = URL.createObjectURL(blob)
            const link = document.createElement('a')
            link.href = url
            link.download = `${courseTitle.replace(/[^a-zA-Z0-9]/g, '_')}_siswa.csv`
            document.body.appendChild(link)
            link.click()
            document.body.removeChild(link)
            URL.revokeObjectURL(url)
        } catch (error) {
            console.error('Failed to export:', error)
            alert('Gagal export data. Silakan coba lagi.')
        }
    }

    const filteredCourses = courses?.filter((course: any) => {
        const matchesType = typeFilter === 'all' || course.courseType === typeFilter
        const courseStatus = course.isPublished ? 'published' : 'draft'
        const matchesStatus = statusFilter === 'all' || courseStatus === statusFilter
        const matchesSearch = course.title?.toLowerCase().includes(searchQuery.toLowerCase())
        return matchesType && matchesStatus && matchesSearch
    }) || []

    return (
        <div className="max-w-[1600px] mx-auto">
            {/* Header Area with Divider */}
            <div className="pb-8 mb-8 border-b border-slate-200 dark:border-white/10">
                <PageHeader
                    title="Manajemen Kursus"
                    subtitle="Kelola semua pelatihan dan materi dalam satu tempat"
                    actions={
                        <Link
                            to="/admin/courses/new"
                            className="flex items-center gap-2 px-5 py-2.5 bg-slate-900 dark:bg-white text-white dark:text-slate-900 rounded-xl font-bold text-sm hover:opacity-90 transition-all shadow-lg shadow-slate-200 dark:shadow-none"
                        >
                            <span className="material-symbols-outlined text-[18px]">add_circle</span>
                            Buat Kursus Baru
                        </Link>
                    }
                />
            </div>

            <div className="px-1">
                {/* Filters */}
                <div className="flex flex-col gap-6 mb-8">
                {/* Search */}
                <div className="relative w-full md:max-w-md group">
                    <span className="absolute left-4 top-1/2 -translate-y-1/2 material-symbols-outlined text-slate-400 group-focus-within:text-primary transition-colors text-[22px]">
                        search
                    </span>
                    <input
                        type="text"
                        placeholder="Cari kursus..."
                        value={searchQuery}
                        onChange={(e) => setSearchQuery(e.target.value)}
                        className="w-full pl-12 pr-4 py-2.5 bg-white dark:bg-slate-900 border border-slate-100 dark:border-white/5 rounded-xl text-sm font-medium text-slate-900 dark:text-white placeholder-slate-400 outline-none focus:border-primary/50 transition-all shadow-sm"
                    />
                </div>

                <div className="flex flex-wrap items-center gap-6">
                    {/* Type Filter */}
                    <div className="flex items-center gap-2 overflow-x-auto pb-1 scrollbar-hide">
                        {(['all', 'pure_online', 'hybrid'] as CourseType[]).map((type) => (
                            <button
                                key={type}
                                onClick={() => setTypeFilter(type)}
                                className={`px-4 py-2 rounded-lg text-xs font-bold transition-all whitespace-nowrap border ${typeFilter === type
                                    ? 'bg-slate-900 dark:bg-white text-white dark:text-slate-900 border-transparent shadow-md'
                                    : 'bg-white dark:bg-slate-900 text-slate-500 dark:text-slate-400 hover:text-slate-900 dark:hover:text-white border-slate-100 dark:border-white/5'
                                    }`}
                            >
                                {type === 'all' ? 'Semua Tipe' : type === 'pure_online' ? 'Online' : 'Hybrid'}
                            </button>
                        ))}
                    </div>

                    {/* Status Filter */}
                    <div className="flex items-center gap-1 bg-slate-100 dark:bg-white/5 p-1 rounded-xl">
                        {(['all', 'published', 'draft'] as CourseStatus[]).map((status) => (
                            <button
                                key={status}
                                onClick={() => setStatusFilter(status)}
                                className={`px-4 py-1.5 rounded-lg text-xs font-bold transition-all ${statusFilter === status
                                    ? 'bg-white dark:bg-slate-800 text-slate-900 dark:text-white shadow-sm'
                                    : 'text-slate-500 hover:text-slate-900 dark:hover:text-white'
                                    }`}
                            >
                                {status === 'all' ? 'Semua' : status.charAt(0).toUpperCase() + status.slice(1)}
                            </button>
                        ))}
                    </div>
                </div>
            </div>

            {/* Courses Table */}
            <div className="bg-white dark:bg-slate-900 rounded-[24px] border border-slate-200 dark:border-white/10 overflow-hidden shadow-sm">
                <div className="overflow-x-auto">
                    <table className="w-full min-w-[1000px]">
                        <thead>
                            <tr className="border-b border-slate-200 dark:border-white/10 bg-slate-50/80 dark:bg-white/[0.03]">
                                <th className="text-left px-6 py-4 text-xs font-black text-slate-700 dark:text-slate-300">Data Kursus</th>
                                <th className="text-left px-4 py-4 text-xs font-black text-slate-700 dark:text-slate-300">Tipe</th>
                                <th className="text-left px-4 py-4 text-xs font-black text-slate-700 dark:text-slate-300">Harga</th>
                                <th className="text-left px-4 py-4 text-xs font-black text-slate-700 dark:text-slate-300 hidden md:table-cell">Pendaftar</th>
                                <th className="text-left px-4 py-4 text-xs font-black text-slate-700 dark:text-slate-300">Status</th>
                                <th className="text-right px-6 py-4 text-xs font-black text-slate-700 dark:text-slate-300">Aksi</th>
                            </tr>
                        </thead>
                        <tbody className="divide-y divide-slate-100 dark:divide-white/5">
                            {isLoading ? (
                                <tr>
                                    <td colSpan={6} className="px-6 py-16 text-center text-slate-400">
                                        <div className="animate-spin inline-block size-8 border-4 border-slate-100 border-t-primary rounded-full mb-4" />
                                        <p className="text-sm font-bold">Memuat data kursus...</p>
                                    </td>
                                </tr>
                            ) : filteredCourses.length === 0 ? (
                                <tr>
                                    <td colSpan={6} className="px-6 py-16 text-center text-slate-500 dark:text-slate-400 font-bold">
                                        Tidak ada kursus yang ditemukan
                                    </td>
                                </tr>
                            ) : (
                                filteredCourses.map((course: any) => (
                                    <tr key={course.id} className="hover:bg-slate-50/50 dark:hover:bg-white/[0.01] transition-all group">
                                        <td className="px-6 py-4">
                                            <div className="flex items-center gap-4">
                                                <div className="size-12 rounded-xl overflow-hidden bg-slate-100 dark:bg-slate-800 flex-shrink-0 shadow-sm border border-slate-200 dark:border-white/10">
                                                    <ImageWithFallback
                                                        src={course.thumbnailUrl}
                                                        alt={course.title}
                                                        className="w-full h-full object-cover group-hover:scale-110 transition-transform duration-700"
                                                    />
                                                </div>
                                                <div className="flex-1 min-w-0">
                                                    <p className="text-sm font-black text-slate-900 dark:text-white group-hover:text-primary transition-colors truncate tracking-tight">{course.title}</p>
                                                </div>
                                            </div>
                                        </td>
                                        <td className="px-4 py-4">
                                            <span className={`px-2.5 py-1 rounded-full text-xs font-bold ${course.courseType === 'hybrid'
                                                ? 'bg-indigo-500 text-white'
                                                : 'bg-emerald-500 text-white'
                                                }`}>
                                                {course.courseType === 'hybrid' ? 'Hybrid' : 'Online'}
                                            </span>
                                        </td>
                                        <td className="px-4 py-4 text-xs font-black text-slate-900 dark:text-white">
                                            {course.price > 0 ? `Rp ${course.price.toLocaleString('id-ID')}` : 'Gratis'}
                                        </td>
                                        <td className="px-4 py-4 hidden md:table-cell">
                                            <div className="flex items-center gap-2 text-slate-600 dark:text-slate-300">
                                                <span className="material-symbols-outlined text-[16px] text-slate-400">group</span>
                                                <span className="text-xs font-black">{course.enrollmentCount || 0}</span>
                                            </div>
                                        </td>
                                        <td className="px-4 py-4">
                                            <span className={`px-2.5 py-1 rounded-full text-xs font-bold ${course.isPublished
                                                ? 'bg-primary text-white'
                                                : 'bg-slate-100 dark:bg-white/5 text-slate-500 dark:text-slate-400'
                                                }`}>
                                                {course.isPublished ? 'Published' : 'Draft'}
                                            </span>
                                        </td>
                                        <td className="px-6 py-4 text-right">
                                            <div className="flex items-center justify-end gap-2">
                                                <button
                                                    onClick={() => handleExportCSV(course.id, course.title)}
                                                    className="size-9 rounded-xl bg-blue-500/10 text-blue-500 flex items-center justify-center hover:bg-blue-500 hover:text-white transition-all"
                                                    title="Export Siswa"
                                                >
                                                    <span className="material-symbols-outlined text-[20px]">download</span>
                                                </button>
                                                {course.isPublished ? (
                                                    <button
                                                        onClick={() => handleUnpublish(course.id)}
                                                        disabled={unpublishCourse.isPending}
                                                        className="size-9 rounded-xl bg-orange-500/10 text-orange-500 flex items-center justify-center hover:bg-orange-500 hover:text-white transition-all disabled:opacity-50"
                                                        title="Unpublish"
                                                    >
                                                        <span className="material-symbols-outlined text-[20px]">visibility_off</span>
                                                    </button>
                                                ) : (
                                                    <button
                                                        onClick={() => handlePublish(course.id)}
                                                        disabled={publishCourse.isPending}
                                                        className="size-9 rounded-xl bg-emerald-500/10 text-emerald-500 flex items-center justify-center hover:bg-emerald-500 hover:text-white transition-all disabled:opacity-50"
                                                        title="Publish"
                                                    >
                                                        <span className="material-symbols-outlined text-[20px]">publish</span>
                                                    </button>
                                                )}
                                                <Link
                                                    to={`/admin/courses/${course.id}/builder`}
                                                    className="size-9 rounded-xl bg-slate-100 dark:bg-white/5 text-slate-500 dark:text-slate-400 flex items-center justify-center hover:bg-slate-900 dark:hover:bg-white hover:text-white dark:hover:text-slate-900 transition-all"
                                                    title="Builder"
                                                >
                                                    <span className="material-symbols-outlined text-[20px]">edit_note</span>
                                                </Link>
                                                <Link
                                                    to={`/admin/courses/${course.id}/edit`}
                                                    className="size-9 rounded-xl bg-slate-100 dark:bg-white/5 text-slate-500 dark:text-slate-400 flex items-center justify-center hover:bg-blue-500 hover:text-white transition-all"
                                                    title="Edit Info Kursus"
                                                >
                                                    <span className="material-symbols-outlined text-[20px]">edit</span>
                                                </Link>
                                                <button
                                                    onClick={() => handleDelete(course.id, course.title)}
                                                    disabled={deleteCourse.isPending}
                                                    className="size-9 rounded-xl bg-slate-100 dark:bg-white/5 text-slate-500 dark:text-slate-400 flex items-center justify-center hover:bg-rose-500 hover:text-white transition-all disabled:opacity-50"
                                                    title="Hapus"
                                                >
                                                    <span className="material-symbols-outlined text-[20px]">delete_sweep</span>
                                                </button>
                                            </div>
                                        </td>
                                    </tr>
                                ))
                            )}
                        </tbody>
                    </table>
                </div>
            </div>
        </div>
    </div>
    )
}

export default AdminCoursesPage
